'use client'

import { FavoriteButtonPrimary } from 'favorite/src/FavoriteButtonPrimary'
import { ClientProductPrice } from 'fukku/Price/client/ClientProductPrice'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import Image from 'next/image'
import { useSticky } from 'pdp/src/components/Sticky/hooks/useSticky'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useColors } from 'pdp/src/contexts/ColorsProvider/ColorsProvider'
import { useSizes } from 'pdp/src/contexts/SizesProvider/SizesProvider'
import type { CommonProps } from 'pdp/src/types/CommonProps'
import { getIsBackInStock } from 'pdp/src/utils/getIsBackInStock/getIsBackInStock'
import { usePdpNewVision } from 'pdp/src/utils/getPdpNewVision/client/usePdpNewVision'
import { ProductLocations } from 'product/types/ProductLocations'
import { type ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import { AddToBagButton } from '../AddToBagButton/AddToBagButton'
import { AddToBagButtonLegacy } from '../AddToBagButton/AddToBagButtonLegacy'
import { BackInStockButton } from '../BackInStockButton/BackInStockButton'
import { ColorItem } from '../ColorsSelector/ColorItem/ColorItem'
import { ColorItemLegacy } from '../ColorsSelector/ColorItem/ColorItemLegacy'

import styles from './Sticky.module.scss'
import text from 'fukku/styles/texts.module.scss'

type StickyProps = Pick<CommonProps, 'product' | 'look'>

const AVATAR_SIZE_WIDTH = 31 // px
const AVATAR_SIZE_HEIGHT = 44 // px

export function Sticky({ product, look }: StickyProps): ReactNode {
	const { scrollToTop, show, colorAlt, image } = useSticky(look)
	const { selectedColor } = useColors()
	const { selectedSize, hasRelatedSizes } = useSizes()
	const { enabled: isNewVision } = usePdpNewVision()
	const { isLessThanLarge, isLargeOrGreater } = useResponsive()
	const {
		country: { hasOnlineSale },
	} = useMasterData()
	const shouldShowBackInStock = getIsBackInStock(
		hasRelatedSizes,
		hasOnlineSale,
		selectedSize,
		selectedColor
	)

	return (
		<ClientPortal selector='sticky-content' show={show}>
			<div className={styles.sticky} aria-hidden>
				<div className={styles.info}>
					<div className={styles.avatar}>
						<Image
							src={image.src}
							alt={image.alt}
							width={AVATAR_SIZE_WIDTH}
							height={AVATAR_SIZE_HEIGHT}
							className={cx(styles.image, styles.avatar)}
							data-testid={dataTestIds['pdp.sticky.image']}
						/>
					</div>
					<p
						className={cx(styles.productName, text.titleM)}
						data-testid={dataTestIds['pdp.sticky.title']}
					>
						{product.name}
					</p>
					<ClientProductPrice
						product={product.id}
						color={selectedColor.id}
						classes={{ price: text.bodyM }}
						align='center'
						dataTestId={dataTestIds['pdp.sticky.price']}
						horizontal
						showDiscount={false}
					/>
					{isLargeOrGreater && selectedSize && (
						<button
							className={styles.button}
							onClick={scrollToTop}
							type='button'
							data-testid={dataTestIds['pdp.sticky.size']}
						>
							<p className={text.bodyM}>{selectedSize?.label}</p>
						</button>
					)}
					<div className={styles.color}>
						<button
							className={styles.button}
							onClick={scrollToTop}
							type='button'
							data-testid={dataTestIds['pdp.sticky.color']}
						>
							{isNewVision ? (
								<ColorItem
									imageAlt={colorAlt}
									imageSrc={selectedColor.bulletImg}
									disabled={!selectedColor.sizes.some((size) => size.available)}
								/>
							) : (
								<ColorItemLegacy
									bordered
									imageAlt={colorAlt}
									imageSrc={selectedColor.bulletImg}
									disabled={!selectedColor.sizes.some((size) => size.available)}
								/>
							)}
						</button>
						<p className={text.bodyM}>{selectedColor.label}</p>
					</div>
				</div>
				<div className={styles.addToBagAndFavorites}>
					{selectedColor.isSellable &&
						(isNewVision ? (
							<AddToBagButton
								product={product}
								look={look}
								onError={scrollToTop}
								location={ProductLocations.Sticky}
								dataTestId={dataTestIds['pdp.sticky.addToBag']}
								className={styles.addToBag}
							/>
						) : (
							<AddToBagButtonLegacy
								product={product}
								look={look}
								onError={scrollToTop}
								location={ProductLocations.Sticky}
								dataTestId={dataTestIds['pdp.sticky.addToBag']}
							/>
						))}
					{shouldShowBackInStock && (
						<BackInStockButton className={styles.backInStock} />
					)}
					<FavoriteButtonPrimary
						className={cx(hasOnlineSale && styles.hideOnMobile)}
						productId={product.id}
						selectedColorId={selectedColor.id}
						showLabel={!selectedColor.isSellable && !isLessThanLarge}
						location={ProductLocations.Sticky}
						dataTestIds={{
							active: dataTestIds['pdp.sticky.favorite.active'],
							inactive: dataTestIds['pdp.sticky.favorite.inactive'],
						}}
						notification
					/>
				</div>
			</div>
		</ClientPortal>
	)
}

type ClientPortalProps = {
	readonly children: JSX.Element
	readonly show: boolean
	readonly selector: string
}

function ClientPortal({ children, selector, show }: ClientPortalProps) {
	const [element, setElement] = useState<HTMLElement | null>(null)

	useEffect(() => {
		setElement(document.getElementById(selector))
	}, [selector])

	return show && element ? createPortal(children, element) : null
}
